import { useMemo, useState } from "react";
import { useUser } from "../contexts/UserContext";
import { useDashboardData } from "../services/queries";
import { RecentScansTable } from "../components";
import { TableSkeleton, Skeleton, Alert, AlertTitle, AlertDescription } from "../components/ui";
import {
  Verdict,
  AttentionQueue,
  Coverage,
  HeartbeatBand,
  RiskPosture,
  GettingStarted,
  groupAlertsByAccount,
  newestAlertAt,
  splitCoverage,
} from "../components/features/dashboard";
import { relativeTime } from "../utils/dates";

const TIMEFRAMES = [
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "90d", label: "Last 90 days" },
];

function greeting() {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

/**
 * The first screen: attention, then coverage, then trend.
 *
 * It used to be an activity report - every section counted something that had
 * already happened, and none said who needed you or what was quietly not being
 * watched. See docs/superpowers/specs/2026-09-03-dashboard-rethink.md.
 *
 * The timeframe now means one thing. Sections it cannot govern (coverage, the
 * roster's risk posture) say their own window on their own label rather than
 * inheriting the page's silently, which is what let the selector appear to
 * control numbers it never touched.
 */
export default function DashboardPage() {
  const { user } = useUser();
  const [timeframe, setTimeframe] = useState("30d");
  const { data, isLoading, error, dataUpdatedAt } = useDashboardData(timeframe);

  const timeframeLabel = TIMEFRAMES.find((t) => t.value === timeframe)?.label || "";

  const stats = data?.stats || {};
  const alerts = useMemo(() => data?.alerts || [], [data]);
  const recentScans = data?.recentScans || [];
  const coverage = useMemo(() => data?.coverage || [], [data]);

  const attention = useMemo(() => groupAlertsByAccount(alerts), [alerts]);
  const newestAt = useMemo(() => newestAlertAt(alerts), [alerts]);
  const staleCount = useMemo(() => splitCoverage(coverage).stale.length, [coverage]);

  if (isLoading) {
    return (
      <main className="p-4 sm:p-8 max-w-7xl mx-auto space-y-6">
        <div>
          <Skeleton className="h-8 w-64" />
          <Skeleton className="mt-2 h-4 w-40" />
        </div>
        <Skeleton className="h-24 w-full rounded-2xl" />
        <Skeleton className="h-40 w-full rounded-2xl" />
        <TableSkeleton rows={5} />
      </main>
    );
  }

  if (error) {
    return (
      <main className="p-4 sm:p-8 max-w-7xl mx-auto">
        <Alert variant="destructive">
          <AlertTitle>We couldn't load your dashboard</AlertTitle>
          <AlertDescription>
            {error.response?.data?.error || "Please try again in a moment."}
          </AlertDescription>
        </Alert>
      </main>
    );
  }

  // A workspace with no accounts has nothing to report on, whatever the
  // timeframe says. Keyed on accounts rather than on a timeframe-scoped scan
  // count, which is what let a workspace whose scans predate the window render
  // "Total scans: 0" above a table of ten scans.
  const accountCount = stats.accounts?.totalAccounts || 0;
  const clientCount = stats.clients?.totalClients || 0;
  const scanCount = stats.scans?.totalScans || 0;

  if (accountCount === 0) {
    return (
      <main className="p-4 sm:p-8 max-w-7xl mx-auto space-y-6">
        <Header user={user} />
        <GettingStarted clientCount={clientCount} accountCount={accountCount} scanCount={scanCount} />
      </main>
    );
  }

  return (
    <main className="p-4 sm:p-8 max-w-7xl mx-auto space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <Header user={user} updatedAt={dataUpdatedAt} />
        <select
          value={timeframe}
          onChange={(e) => setTimeframe(e.target.value)}
          aria-label="Timeframe"
          className="rounded-lg border border-input bg-card px-3 py-2 text-sm text-foreground hover:border-input focus:border-transparent focus:outline-none focus:ring-2 focus:ring-brand-500"
        >
          {TIMEFRAMES.map((t) => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>
      </div>

      <Verdict
        accountsNeedingAttention={attention}
        newestAlertAt={newestAt}
        staleCount={staleCount}
      />

      {attention.length > 0 && (
        <section className="space-y-3">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="text-base font-semibold text-foreground">Needs your attention</h2>
            <span className="text-sm text-muted-foreground">
              {attention.length} account{attention.length === 1 ? "" : "s"} · {timeframeLabel}
            </span>
          </div>
          <AttentionQueue rows={attention} />
        </section>
      )}

      <Coverage accounts={coverage} />

      <HeartbeatBand />

      <RiskPosture accounts={stats.accounts} scans={stats.scans} />

      <section className="space-y-3">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-base font-semibold text-foreground">Recent scans</h2>
          <span className="text-sm text-muted-foreground">{timeframeLabel}</span>
        </div>
        <RecentScansTable scans={recentScans} />
      </section>
    </main>
  );
}

function Header({ user, updatedAt }) {
  return (
    <div>
      {/* The greeting used to be text-3xl, the largest type on the page, spent
          on the one line that carries no information. The verdict directly
          below it ("Nothing needs your attention" / "One account needs
          attention") is the actual headline, and the two were competing. The
          greeting stays, because a daily tool should say hello, but it is
          sized like the courtesy it is and lets the verdict lead. */}
      <h1 className="text-base font-semibold text-foreground">
        {greeting()}, {user?.name || "there"}
      </h1>
      <p className="mt-0.5 text-sm text-muted-foreground">
        {/* The page used to print today's date here, which reads as freshness
            and is not - the data is cached. This says when it was actually
            fetched. */}
        {updatedAt ? `Updated ${relativeTime(updatedAt)}` : "Loading your workspace"}
      </p>
    </div>
  );
}
